import { useEffect, useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Star } from "lucide-react";
import Navbar from "../components/Navbar";
import ServiceBadges from "../components/ServiceBadges";
import Disclaimer from "../components/Disclaimer";
import Avatar from "../components/Avatar";
import PosterRow from "../components/PosterRow";
import GenreChipRow from "../components/GenreChipRow";
import PodcastSpotlight from "../components/PodcastSpotlight";
import IndieTeaser from "../components/IndieTeaser";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { stopSong, armAutoplay } from "../lib/profileSong";
import { useTop8 } from "../context/Top8Context";
import { toast } from "sonner";

const SVC = { netflix: "NETFLIX", hulu: "HULU", appletv: "APPLE TV+", disney: "DISNEY+", max: "MAX", prime: "PRIME VIDEO", paramount: "PARAMOUNT+", peacock: "PEACOCK", youtubetv: "YOUTUBE TV" };

const Divider = () => <div className="glitter-divider my-5 sm:my-4" data-testid="glitter-divider" aria-hidden />;
const Header = ({ children, testid }) => (
  <h2 className="social-head" data-testid={testid}><span className="social-star">★</span>{children}<span className="social-star">★</span></h2>
);

function epLabel(id) {
  let h = 0; for (const c of String(id)) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  const s = (h % 4) + 1, e = (h % 9) + 1, m = 8 + (h % 48);
  return `S${s} • E${e} • ${m}m left`;
}

export default function SocialHome() {
  const navigate = useNavigate();
  const { user, patchUser } = useAuth();
  const { removeFromTop8 } = useTop8();
  const [rows, setRows] = useState([]);
  const [cw, setCw] = useState([]);
  const [indie, setIndie] = useState({ teaser: false, items: [] });
  const [top8Items, setTop8Items] = useState([]);
  const [mood, setMood] = useState(() => user?.mood || "watching everything ✨");
  const [visits, setVisits] = useState(0);
  const [friends, setFriends] = useState([]);
  const [lovedItems, setLovedItems] = useState([]);

  useEffect(() => {
    api.get("/friends").then(({ data }) => setFriends(data.friends || [])).catch(() => {});
    api.get("/catalog/loved-by-friends").then(({ data }) => setLovedItems(data.items || [])).catch(() => {});
  }, []);

  useEffect(() => { if (user?.mood != null && user.mood !== "") setMood(user.mood); }, [user?.mood]);

  const saveMood = () => {
    const m = mood.trim();
    if (m === (user?.mood || "")) return;
    api.put("/user/profile", { mood: m }).then(({ data }) => patchUser(data)).catch(() => {});
  };

  useEffect(() => {
    armAutoplay();
    // real, persisted visitor count (odometer)
    const base = 13370;
    const n = parseInt(localStorage.getItem("fh_social_visits") || "0", 10) + 1;
    localStorage.setItem("fh_social_visits", String(n));
    setVisits(base + n);
    return () => { stopSong(); }; // stop the chiptune when leaving the home screen
  }, []);

  useEffect(() => {
    let alive = true;
    api.get("/catalog/home").then(({ data }) => {
      if (!alive) return;
      setRows((data.rows || []).filter((r) => r.key !== "continue_watching" && r.key !== "indie_spotlight"));
      setCw((data.rows || []).find((r) => r.key === "continue_watching")?.items || []);
      setIndie({ teaser: !!data.indie_teaser, items: (data.rows || []).find((r) => r.key === "indie_spotlight")?.items || [] });
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const t8Key = (user?.top8 || []).join(",");
  useEffect(() => {
    let alive = true;
    const ids = user?.top8 || [];
    if (!ids.length) { setTop8Items([]); return; }
    api.get("/top8").then(({ data }) => { if (alive) setTop8Items(data.items || []); }).catch(() => {});
    return () => { alive = false; };
  }, [t8Key]);

  const hero = rows[0]?.items?.[0];
  const heroSvc = (user?.services || [])[0];
  const open = (id) => { stopSong(); navigate(`/title/${id}`); };

  const top8 = useMemo(() => {
    const slots = [...top8Items];
    while (slots.length < 8) slots.push(null);
    return slots.slice(0, 8);
  }, [top8Items]);

  const promote = (item) => {
    const ids = user?.top8 || [];
    const idx = ids.indexOf(item.id);
    if (idx <= 0) return;
    const next = [...ids];
    const [id] = next.splice(idx, 1);
    next.unshift(id);
    patchUser({ top8: next });
    setTop8Items((prev) => {
      const i = prev.findIndex((x) => x.id === item.id);
      if (i <= 0) return prev;
      const n = [...prev];
      const [it] = n.splice(i, 1);
      n.unshift(it);
      return n;
    });
    api.put("/top8", { ids: next }).then(() => toast.success("Moved to the top of your Top 8!")).catch(() => {});
  };

  const bulletin = (rows[0]?.items || []).slice(0, 6);

  return (
    <div className="min-h-screen" data-testid="social-home" data-skin-home="social">
      <Navbar />
      <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6">
        <GenreChipRow className="mb-5" wrap />
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[300px_minmax(0,1fr)] lg:gap-5">
          {/* ===== Profile sidebar ===== */}
          <aside className="space-y-5 lg:space-y-4" data-testid="social-sidebar">
            <section className="social-panel" data-testid="social-profile-card">
              <Header testid="social-profile-head">{user?.name || "My"} Profile</Header>
              <div className="flex items-center gap-3 p-3">
                <div className="social-avatar-frame" data-testid="social-avatar">
                  <Avatar user={user} size={64} rounded="rounded-[3px]" />
                </div>
                <div className="min-w-0">
                  <p className="truncate font-bold text-[var(--text-primary)]">{user?.name || "The Dial"}</p>
                  <p className="social-online"><span className="online-dot" /> ONLINE NOW</p>
                </div>
              </div>
              <p className="px-3 pb-2 text-sm italic text-[var(--text-secondary)]" data-testid="social-mood">
                Mood: <input value={mood} onChange={(e) => setMood(e.target.value)} onBlur={saveMood} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} className="w-40 border-b border-dotted border-[var(--border-subtle)] bg-transparent italic text-[var(--accent)] outline-none" data-testid="social-mood-input" />
              </p>
              <ul className="space-y-1 px-3 pb-3 text-sm text-[var(--text-secondary)]">
                <li>★ Last Login: <b>{new Date().toLocaleString()}</b></li>
                <li>★ Friends: <b>{(user?.following || []).length}</b></li>
                <li>★ Top 8: <b data-testid="social-top8-count">{top8.filter(Boolean).length}/8</b></li>
                <li>★ Location: <b>The Internet</b></li>
              </ul>
              <div className="mx-3 mb-3 rounded border border-dotted border-[var(--border-subtle)] bg-black/80 p-2 text-center">
                <p className="text-[10px] uppercase tracking-widest text-[var(--glitter-cyan)]">Visitors</p>
                <p className="visitor-counter" data-testid="social-visitor-counter">{String(visits).padStart(7, "0").split("").map((d, i) => <span key={i} className="odo-digit">{d}</span>)}</p>
              </div>
              <div className="flex flex-col gap-1 px-3 pb-3 text-sm">
                <button onClick={() => toast("Messages are coming soon ✉")} className="social-link text-left" data-testid="social-send-message">» Send Message</button>
                <Link to="/friends" className="social-link text-left" data-testid="social-friends-link">» My Friends</Link>
                <Link to="/friends/requests" className="social-link text-left" data-testid="social-requests-link">» Friend Requests</Link>
                {user?.username && <Link to={`/profile/${user.username}`} className="social-link text-left" data-testid="social-view-profile">» View My Public Profile</Link>}
              </div>
            </section>

            <section className="social-panel" data-testid="social-blurb">
              <Header>The Dial's Blurb</Header>
              <p className="p-3 text-sm text-[var(--text-secondary)]">Every service you pay for, one guide. We're the guide, not the channel — press play on the real app. ✨📺</p>
            </section>

            <section className="social-panel" data-testid="social-comments">
              <Header>Comments</Header>
              <ul className="space-y-2 p-3 text-sm">
                {[["xXmovie_buffXx", "ur Top 8 is elite 🔥"], ["dvd_dani", "add me!! lol"], ["popcorn_pete", "thanks for the rec ★★★★★"]].map(([u, c], i) => (
                  <li key={i} className="border-b border-dotted border-[var(--border-subtle)] pb-2">
                    <span className="social-link font-bold">{u}</span>
                    <p className="text-[var(--text-secondary)]">{c}</p>
                  </li>
                ))}
              </ul>
            </section>
          </aside>

          {/* ===== Main column ===== */}
          <div className="space-y-6 lg:space-y-4">
            {hero && (
              <section className="social-panel overflow-hidden" data-testid="social-hero">
                <Divider />
                <Header testid="social-hero-head">Hero Spotlight</Header>
                <div className="relative m-3 overflow-hidden rounded border-2 border-dotted border-[var(--border-accent)]">
                  <img src={hero.img} alt={hero.title} className="h-44 w-full object-cover sm:h-56" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-4">
                    <h1 className="social-hero-title">{hero.title}</h1>
                    <p className="mt-1 text-xs font-bold uppercase tracking-widest text-[var(--glitter-cyan)]" data-testid="social-now-streaming">
                      Now Streaming{heroSvc ? ` on ${SVC[heroSvc] || heroSvc.toUpperCase()}` : ""}
                    </p>
                    <div className="mt-1 flex items-center gap-2 text-sm text-white">
                      <span className="flex items-center gap-1 text-amber-300"><Star size={14} className="fill-amber-300" />{(hero.rating || 0).toFixed(1)}</span>
                      {hero.year && <span>{hero.year}</span>}
                    </div>
                    <button onClick={() => open(hero.id)} className="social-bracket-btn mt-2" data-testid="social-watch-now">[ WATCH NOW ]</button>
                  </div>
                </div>
                <Divider />
              </section>
            )}

            {/* Top 8 */}
            <section className="social-panel" data-testid="social-top8">
              <Header testid="social-top8-head">My Top 8</Header>
              <div className="grid grid-cols-2 gap-x-4 gap-y-6 p-4 sm:grid-cols-4 sm:gap-4 sm:p-3">
                {top8.map((it, i) => it ? (
                  <div key={it.id} className="group relative" data-testid={`social-top8-slot-${i}`}>
                    <span className="social-rank">{i + 1}</span>
                    <button onClick={() => open(it.id)} className="block w-full overflow-hidden rounded border border-dotted border-[var(--border-subtle)] bg-white">
                      <div className="aspect-[2/3] overflow-hidden"><img src={it.poster || it.img} alt={it.title} className="h-full w-full object-cover" /></div>
                    </button>
                    <p className="mt-1.5 truncate text-sm font-bold text-[var(--text-primary)]" title={it.title}>{it.title}</p>
                    <p className="social-online mt-0.5 text-[10px]"><span className="online-dot" /> ONLINE NOW</p>
                    <ServiceBadges id={it.id} className="mt-1 w-fit" />
                    <div className="mt-1 flex items-center gap-3">
                      {i > 0 && <button onClick={() => promote(it)} className="social-link inline-flex min-h-[32px] items-center text-xs" data-testid={`social-top8-promote-${i}`}>▲ rank up</button>}
                      <button onClick={() => removeFromTop8(it)} className="inline-flex min-h-[32px] items-center text-xs font-bold text-[var(--accent)] hover:underline" data-testid={`social-top8-remove-${i}`}>✕ remove</button>
                    </div>
                  </div>
                ) : (
                  <div key={`empty-${i}`} className="flex min-h-[132px] aspect-[2/3] items-center justify-center rounded border border-dashed border-[var(--border-subtle)] text-center text-[11px] leading-relaxed text-[var(--text-muted)]" data-testid={`social-top8-empty-${i}`}>
                    <span>#{i + 1}<br />add a fave</span>
                  </div>
                ))}
              </div>
            </section>

            {/* Continue Watching filmstrip */}
            {cw.length > 0 && (
              <section className="social-panel" data-testid="social-filmstrip">
                <Header>Continue Watching</Header>
                <div className="flex gap-2 overflow-x-auto p-3">
                  {cw.map((it) => (
                    <button key={it.id} onClick={() => open(it.id)} className="filmstrip-cell w-36 flex-none text-left" data-testid={`social-cw-${it.id}`}>
                      <div className="aspect-video overflow-hidden"><img src={it.img || it.poster} alt={it.title} className="h-full w-full object-cover" /></div>
                      <p className="truncate px-1 pt-1 text-xs font-bold text-[var(--text-primary)]">{it.title}</p>
                      <p className="px-1 pb-1 font-mono text-[10px] text-[var(--accent)]">{epLabel(it.id)}</p>
                    </button>
                  ))}
                </div>
              </section>
            )}

            {/* Bulletin */}
            <section className="social-panel" data-testid="social-bulletin">
              <Header testid="social-bulletin-head">Recent Updates &amp; Bulletin</Header>
              <ul className="space-y-1.5 p-3 text-sm">
                {bulletin.map((it) => (
                  <li key={it.id}>
                    <button onClick={() => open(it.id)} className="text-left"><span className="social-star">★</span> New title added: <span className="social-link font-bold">{it.title}</span> <span className="text-[var(--text-muted)]">({it.year})</span></button>
                  </li>
                ))}
                <li><span className="social-star">★</span> <span className="social-link font-bold">dvd_dani</span> just updated their Top 8</li>
                <li><span className="social-star">★</span> <span className="social-link font-bold">popcorn_pete</span> is now ONLINE</li>
              </ul>
            </section>

            {/* Friend Space */}
            {friends.length > 0 && (
              <section className="social-panel" data-testid="social-friends">
                <Header testid="social-friends-head">My Friend Space</Header>
                <p className="px-4 pt-1 text-xs text-[var(--text-muted)]">{friends.length} {friends.length === 1 ? "Friend" : "Friends"}</p>
                <div className="grid grid-cols-4 gap-x-4 gap-y-5 p-4 sm:grid-cols-8 sm:gap-3">
                  {friends.slice(0, 8).map((f) => (
                    <Link key={f.id} to={`/profile/${f.username}`} className="group block text-center" data-testid={`social-friend-${f.username}`}>
                      <div className="social-avatar-frame mx-auto w-fit"><Avatar user={f} size={64} rounded="rounded-[3px]" /></div>
                      <p className="mt-1.5 truncate text-xs font-bold text-[var(--accent)] group-hover:underline" title={f.name}>{f.name}</p>
                    </Link>
                  ))}
                </div>
                <div className="px-4 pb-3">
                  <Link to="/friends" className="social-link text-sm" data-testid="social-friends-viewall">» View all friends</Link>
                </div>
              </section>
            )}
          </div>
        </div>

        {lovedItems.length > 0 && (
          <PosterRow title="Loved by Friends" items={lovedItems} testid="row-loved_by_friends" />
        )}

        {indie.items.length > 0 && <PosterRow title="Indie Spotlight" items={indie.items} testid="row-indie_spotlight" seeAll="/indie" />}
        {indie.teaser && <IndieTeaser />}
        <PodcastSpotlight />

        <footer className="social-footer" data-testid="social-footer">
          The Dial © 2005 · <span className="social-link">About</span> · <span className="social-link">Terms</span> · <span className="social-link">Privacy</span> · <span className="social-link">Help</span> · Made with ★ &amp; HTML
        </footer>
      </main>
      <Disclaimer />
    </div>
  );
}
